import React, { useMemo, useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const EmployeeDistributionList = ({
  employees = [],
  onAmountChange,
  isProcessing,
  totalAmount = 0,
  onOverrideNotesChange,
  readOnly = false,
}) => {
  // Track which employees have been modified and their notes
  const [overrideNotes, setOverrideNotes] = useState({});
  const [focusedInput, setFocusedInput] = useState(null);
  // Track raw input values to allow typing decimals
  const [inputValues, setInputValues] = useState({});

  // Store original amounts to detect changes
  const originalAmounts = useMemo(() => {
    const amounts = {};
    employees.forEach((emp) => {
      if (!amounts[emp.id]) {
        amounts[emp.id] = emp.calculatedAmount || 0;
      }
    });
    return amounts;
  }, [employees]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount || 0);
  };

  // Format hours worked
  const formatHours = (hours) => {
    if (hours == null || hours === undefined) return null;
    const numHours = parseFloat(hours);
    if (isNaN(numHours)) return null;
    return numHours.toFixed(1);
  };

  // Calculate totals
  const totals = useMemo(() => {
    const totalAllocated = employees.reduce(
      (sum, emp) => sum + (emp.calculatedAmount || 0),
      0
    );
    return {
      allocated: totalAllocated,
      remaining: totalAmount - totalAllocated,
      isBalanced: Math.abs(totalAmount - totalAllocated) < 0.01,
    };
  }, [employees, totalAmount]);

  // Do NOT sort employees - keep original order
  const displayEmployees = employees;

  // Check if amount has been modified
  const isAmountModified = (employeeId, currentAmount) => {
    if (readOnly) return false;
    const original = originalAmounts[employeeId];
    return original !== undefined && Math.abs(original - currentAmount) > 0.01;
  };

  // Get employees with modified amounts that need notes
  const employeesNeedingNotes = useMemo(() => {
    if (readOnly) return [];
    return displayEmployees
      .filter((emp) => {
        const modified = isAmountModified(emp.id, emp.calculatedAmount || 0);
        const hasNote = overrideNotes[emp.id]?.trim().length > 0;
        return modified && !hasNote;
      })
      .map((e) => e.name);
  }, [displayEmployees, overrideNotes, originalAmounts, readOnly]);

  const handleAmountInputChange = (employeeId, value) => {
    if (readOnly) return;

    // Store the raw input value to allow typing decimals
    setInputValues((prev) => ({ ...prev, [employeeId]: value }));

    // Remove currency symbols and commas, but keep decimal points
    const cleanValue = value.replace(/[£,]/g, "").trim();

    // Allow empty string, numbers, and partial decimals (e.g., "12.", "12.5")
    if (cleanValue === "" || cleanValue === ".") {
      return;
    }

    // Check if it's a valid number pattern (allows trailing decimal)
    const isValidPattern = /^-?\d*\.?\d*$/.test(cleanValue);
    if (!isValidPattern) {
      return;
    }

    // Only update parent if it's a complete valid number
    const numericValue = parseFloat(cleanValue);
    if (!isNaN(numericValue) && numericValue >= 0) {
      onAmountChange(employeeId, numericValue);
    }
  };

  const handleAmountBlur = (employeeId, value) => {
    if (readOnly) return;

    const cleanValue = value.replace(/[£,]/g, "").trim();

    // Clear the raw input value on blur
    setInputValues((prev) => {
      const newValues = { ...prev };
      delete newValues[employeeId];
      return newValues;
    });

    // If empty or invalid on blur, set to 0
    if (cleanValue === "" || cleanValue === "." || isNaN(parseFloat(cleanValue))) {
      onAmountChange(employeeId, 0);
    } else {
      // Ensure we have a proper number with 2 decimal places
      const numericValue = parseFloat(cleanValue);
      if (!isNaN(numericValue)) {
        onAmountChange(employeeId, Math.round(numericValue * 100) / 100);
      }
    }

    setFocusedInput(null);
  };

  const handleAmountFocus = (employeeId) => {
    if (readOnly) return;
    setFocusedInput(employeeId);
    // Initialize raw input with current value
    const employee = employees.find((e) => e.id === employeeId);
    if (employee) {
      setInputValues((prev) => ({
        ...prev,
        [employeeId]: String(employee.calculatedAmount || 0),
      }));
    }
  };

  const handleNoteChange = (employeeId, note) => {
    if (readOnly) return;

    const newNotes = {
      ...overrideNotes,
      [employeeId]: note,
    };
    setOverrideNotes(newNotes);

    if (onOverrideNotesChange) {
      onOverrideNotesChange(newNotes);
    }
  };

  // Get display value for input
  const getInputValue = (employee) => {
    // If we have a raw input value (user is typing), use that
    if (inputValues[employee.id] !== undefined) {
      return inputValues[employee.id];
    }

    if (!readOnly && focusedInput === employee.id) {
      return String(employee.calculatedAmount || 0);
    }
    return (employee.calculatedAmount || 0).toFixed(2);
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Icon name="Users" size={20} className="text-secondary" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              {readOnly ? "Simulated Allocations" : "Employee Allocations"}
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              {employees.length} eligible employees •{" "}
              {formatCurrency(totals.allocated)} allocated
            </p>
          </div>
        </div>
        {/* Total Badge */}
        <div
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
            totals.isBalanced
              ? "bg-success/10 text-success"
              : "bg-warning/10 text-warning"
          }`}
        >
          {totals.isBalanced
            ? "✓ Balanced"
            : `${formatCurrency(Math.abs(totals.remaining))} ${
                totals.remaining > 0 ? "remaining" : "over"
              }`}
        </div>
      </div>

      {/* Info banner - Only show in edit mode */}
      {!readOnly && (
        <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-start gap-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <p className="text-xs text-muted-foreground">
            You can manually adjust any employee's amount.{" "}
            <strong>If you change an amount, you must provide a reason.</strong> The
            total must equal{" "}
            <strong>{formatCurrency(totalAmount)}</strong> before approval.
          </p>
        </div>
      )}

      {/* Lab mode banner */}
      {readOnly && (
        <div className="mb-4 p-3 bg-info/5 border border-info/20 rounded-lg flex items-start gap-3">
          <Icon name="FlaskConical" size={16} className="text-info mt-0.5" />
          <p className="text-xs text-muted-foreground">
            This is a simulation preview. These amounts are for testing purposes only and cannot be edited or saved.
          </p>
        </div>
      )}

      {/* Validation Warning for Missing Notes - Only in edit mode */}
      {!readOnly && employeesNeedingNotes.length > 0 && (
        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
          <Icon name="AlertTriangle" size={16} className="text-error mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-error mb-1">
              Notes Required
            </p>
            <p className="text-xs text-error">
              You must provide a reason for modifying the amounts for:
              {employeesNeedingNotes.join(", ")}
            </p>
          </div>
        </div>
      )}

      {/* Employee List */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
        {displayEmployees.map((employee, index) => {
          const percentage =
            totalAmount > 0
              ? ((employee.calculatedAmount || 0) / totalAmount) * 100
              : 0;
          const isModified = isAmountModified(
            employee.id,
            employee.calculatedAmount || 0
          );
          const hasNote = overrideNotes[employee.id]?.trim().length > 0;
          const needsNote = !readOnly && isModified && !hasNote;
          const hoursWorked = formatHours(employee.hoursWorked);

          return (
            <div
              key={employee.id}
              className={`bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors ${
                needsNote ? "border-2 border-error" : ""
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Rank Badge */}
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground flex-shrink-0 mt-1">
                  {index + 1}
                </div>

                {/* Avatar */}
                <Image
                  src={employee.avatar}
                  alt={employee.name}
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0"
                />

                {/* Info - Flex Column */}
                <div className="flex-1 min-w-0 flex flex-col gap-3">
                  {/* Employee Details */}
                  <div>
                    <h3 className="font-semibold text-sm md:text-base truncate">
                      {employee.name}
                      {!readOnly && isModified && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-warning">
                          <Icon name="Edit3" size={12} />
                          Modified
                        </span>
                      )}
                    </h3>
                    {/* Job Role - Department - Hours Row */}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Icon name="Briefcase" size={12} />
                        {employee.role || employee.jobRoleName || 'No Role'}
                      </span>
                      {employee.department && (
                        <>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1">
                            <Icon name="Building2" size={12} />
                            {employee.department}
                          </span>
                        </>
                      )}
                      {hoursWorked && (
                        <>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1">
                            <Icon name="Clock" size={12} />
                            {hoursWorked}h worked
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Amount Display/Input and Percentage */}
                  <div className="flex items-center gap-3">
                    {/* Percentage */}
                    <div className="flex flex-col items-center px-3 py-2 bg-muted rounded-lg">
                      <span className="text-xs text-muted-foreground">Share</span>
                      <span className="font-semibold text-sm">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>

                    {/* Hours Worked Badge - if available */}
                    {hoursWorked && (
                      <div className="flex flex-col items-center px-3 py-2 bg-info/10 rounded-lg">
                        <span className="text-xs text-muted-foreground">Hours</span>
                        <span className="font-semibold text-sm text-info">
                          {hoursWorked}h
                        </span>
                      </div>
                    )}

                    {/* Amount - Editable or Read-Only */}
                    {readOnly ? (
                      <div className="flex-1 flex flex-col">
                        <span className="text-xs text-muted-foreground mb-1">Amount</span>
                        <div className="px-3 py-2 bg-muted rounded-lg font-bold font-data text-success text-right">
                          {formatCurrency(employee.calculatedAmount || 0)}
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col">
                        <label className="text-xs text-muted-foreground mb-1">
                          Amount{" "}
                          {isModified && (
                            <span className="text-warning">*</span>
                          )}
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold">
                            £
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={getInputValue(employee)}
                            onChange={(e) =>
                              handleAmountInputChange(employee.id, e.target.value)
                            }
                            onFocus={() => handleAmountFocus(employee.id)}
                            onBlur={(e) => handleAmountBlur(employee.id, e.target.value)}
                            disabled={isProcessing}
                            className={`input input-sm w-full pl-7 pr-3 text-right font-bold font-data ${
                              isModified ? "text-warning border-warning" : "text-success"
                            } ${needsNote ? "border-error" : ""}`}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Override Note - Navy gradient styling, shown above progress bar */}
                  {!readOnly && isModified && (
                    <div
                      className="flex flex-col gap-2 p-3 rounded-lg border border-primary/30"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(30, 58, 95, 0.15) 0%, rgba(30, 58, 95, 0.25) 100%)",
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="FileText" size={14} className="text-primary" />
                        <label className="text-xs font-semibold text-primary">
                          Reason for Change (Required) *
                        </label>
                      </div>
                      <textarea
                        value={overrideNotes[employee.id] || ""}
                        onChange={(e) => handleNoteChange(employee.id, e.target.value)}
                        disabled={isProcessing}
                        placeholder="Explain why this amount was changed..."
                        className={`input w-full resize-none text-sm ${
                          needsNote ? "border-error" : "border-primary/30"
                        }`}
                        rows={2}
                        required
                      />
                      {needsNote && (
                        <p className="text-xs text-error flex items-center gap-1">
                          <Icon name="AlertCircle" size={12} />
                          You must provide a reason for this change
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Info" size={12} />
                        <span>
                          Original:{" "}
                          {formatCurrency(originalAmounts[employee.id])} → Changed:{" "}
                          {formatCurrency(employee.calculatedAmount || 0)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Progress Bar - Navy gradient */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(percentage, 100)}%`,
                          background:
                            !readOnly && isModified
                              ? "linear-gradient(90deg, #f59e0b 0%, #d97706 100%)"
                              : "linear-gradient(90deg, #1e3a5f 0%, #2d4a6f 50%, #3d5a7f 100%)",
                        }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-12 text-right">
                      {percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Total Pool</p>
            <p className="font-bold font-data">{formatCurrency(totalAmount)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Allocated</p>
            <p
              className={`font-bold font-data ${
                totals.isBalanced ? "text-success" : "text-warning"
              }`}
            >
              {formatCurrency(totals.allocated)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              {totals.remaining >= 0 ? "Remaining" : "Over Allocated"}
            </p>
            <p
              className={`font-bold font-data ${
                totals.isBalanced ? "text-success" : "text-error"
              }`}
            >
              {totals.remaining >= 0 ? "" : "-"}{" "}
              {formatCurrency(Math.abs(totals.remaining))}
            </p>
          </div>
        </div>

        {/* Balance Warning - Only in edit mode */}
        {!readOnly && !totals.isBalanced && (
          <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg flex items-center gap-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <p className="text-sm text-warning">
              {totals.remaining > 0
                ? `You must allocate the remaining ${formatCurrency(
                    totals.remaining
                  )} before approval.`
                : `You have over-allocated by ${formatCurrency(
                    Math.abs(totals.remaining)
                  )}. Reduce amounts to match the total.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDistributionList;
